import React, {useState} from "react";
import {Col, Row, Tabs, Tab, Button} from "react-bootstrap";
import {useParams} from "react-router-dom";
import {StockTransactionTable} from "./StockTransactionTable";
import {CostTransactionTable} from "./CostTransactionTable";
import {AccountingTransactionTable} from "./AccountingTransactionTable";
import {DocumentTransactionDto} from "../../../dtos/transactions/DocumentTransactionDto";
import {CashTransactionTable} from "./CashTransactionTable";
import {BankTransactionTable} from "./BankTransactionTable";
import {SalesTransactionTable} from "./SalesTransactionTable";
import {PurchaseTransactionTable} from "./PurchaseTransactionTable";
import {SettlementCustomerByDocumentMovementTable} from "./SettlementCustomerByDocumentMovementTable";
import {SettlementVendorByDocumentMovementTable} from "./SettlementVendorByDocumentMovementTable";
import {MoneyToPayTransactionTable} from "./MoneyToPayTransactionTable";
import {OrderTransactionTable} from "./OrderTransactionTable";
import {PurchaseOrderTransactionTable} from "./PurchaseOrderTransactionTable";
import {FixedAssetsInitialInfoTransactionRecordTable} from "./FixedAssetsInitialInfoTransactionRecordTable";
import {FixedAssetsCurrentInfoTransactionRecordTable} from "./FixedAssetsCurrentInfoTransactionRecordTable";
import {ConstructionObjectTransactionRecordTable} from "./ConstructionObjectTransactionRecordTable";
import {FixedAssetDepreciationTransactionRecordTable} from "./FixedAssetDepreciationTransactionRecordTable";
import Loader from "../../../assets/img/svg/Loader";
import {useTranslation} from "react-i18next";
import {PriceListTransactionTable} from "./PriceListTransactionTable";
import {OtherExpenseTransactionTable} from "./OtherExpenseTransactionTable";
import {OtherIncomeTransactionTable} from "./OtherIncomeTransactionTable";
import {SettlementVendorMovementTable} from "./SettlementVendorMovementTable";
import {SettlementCustomerMovementTable} from "./SettlementCustomerMovementTable";
import {MoneyToPayCashTransactionRecordTable} from "./MoneyToPayCashTransactionRecordTable";
import {MoneyToPayBankTransactionRecordTable} from "./MoneyToPayBankTransactionRecordTable";
import {SettlementAdvanceHolderTransactionRecordTable} from "./SettlementAdvanceHolderTransactionRecordTable";
import {CurrentEmployeeTransactionRecordTable} from "./CurrentEmployeeTransactionRecordTable";
import {DismissalEmployeeTransactionRecordTable} from "./DismissalEmployeeTransactionRecordTable";
import {HireEmployeeTransactionRecordTable} from "./HireEmployeeTransactionRecordTable";
import {HistoryEmployeeStateTransactionRecordTable} from "./HistoryEmployeeStateTransactionRecordTable";
import {TransferEmployeeTransactionRecordTable} from "./TransferEmployeeTransactionRecordTable";
import {AccruedVatTransactionRecordTable} from "./AccruedVatTransactionRecordTable";
import {PurchasedVatTransactionRecordTable} from "./PurchasedVatTransactionRecordTable";
import {DepositLoanTransactionRecordTable} from "./DepositLoanTransactionRecordTable";
import {SalaryTransactionRecordTable} from "./SalaryTransactionRecordTable";
import {SalaryPaymentTransactionRecordTable} from "./SalaryPaymentTransactionRecordTable";
import {AccrualTransactionRecordTable} from "./AccrualTransactionRecordTable";
import {EmployeeSettlementTransactionRecordTable} from "./EmployeeSettlementTransactionRecordTable";
import {StockReservationTransactionRecordTable} from "./StockReservationTransactionRecordTable";
import {GoodsToReceiveTransactionRecordTable} from "./GoodsToReceiveTransactionRecordTable";
import {TaxInvoiceJournalTransactionRecordTable} from "./TaxInvoiceJournalTransactionRecordTable";
import {DistributionOfLandedCostTransactionRecordTable} from "./DistributionOfLandedCostTransactionRecordTable";

interface IProps {
    documentTransactions: DocumentTransactionDto;
}

interface ITransactionTab {
    title: string;
    collectionName: string;
    component: any;
    visible: boolean;
}

const TransactionsPage = ({documentTransactions}: IProps) => {
    const {documentId} = useParams();
    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const transactionTabs: ITransactionTab[] = [
        {
            title: t("Accounting"),
            collectionName: "accountingTransactionRecordDtos",
            component: AccountingTransactionTable,
            visible: true
        },
        {
            title: t("Stock"),
            collectionName: "stockTransactionRecordDtos",
            component: StockTransactionTable,
            visible: true
        },
        {
            title: t("Cost"),
            collectionName: "costTransactionRecordDtos",
            component: CostTransactionTable,
            visible: true
        },
        {
            title: t("Cash"),
            collectionName: "cashTransactionDtos",
            component: CashTransactionTable,
            visible: true
        },
        {
            title: t("Bank"),
            collectionName: "bankTransactionRecordDtos",
            component: BankTransactionTable,
            visible: true
        },
        {
            title: t("Sales"),
            collectionName: "saleTransactionDtos",
            component: SalesTransactionTable,
            visible: true
        },
        {
            title: t("Purchase"),
            collectionName: "purchaseTransactionDtos",
            component: PurchaseTransactionTable,
            visible: true
        },
        {
            title: t("SettlementCustomerByDocument"),
            collectionName: "settlementCustomerByDocumentMovementTransactionDtos",
            component: SettlementCustomerByDocumentMovementTable,
            visible: true
        },
        {
            title: t("SettlementVendorByDocument"),
            collectionName: "settlementVendorByDocumentMovementTransactionDtos",
            component: SettlementVendorByDocumentMovementTable,
            visible: true
        },
        {
            title: t("SettlementCustomer"),
            collectionName: "settlementCustomerTransactionRecordDtos",
            component: SettlementCustomerMovementTable,
            visible: true
        },
        {
            title: t("SettlementVendor"),
            collectionName: "settlementVendorTransactionRecordDtos",
            component: SettlementVendorMovementTable,
            visible: true
        },
        {
            title: t("MoneyToPay"),
            collectionName: "moneyToPayTransactionRecordDtos",
            component: MoneyToPayTransactionTable,
            visible: true
        },
        {
            title: t("SalesOrderTransaction"),
            collectionName: "orderTransactionRecordDtos",
            component: OrderTransactionTable,
            visible: true
        },
        {
            title: t("PurchaseOrder"),
            collectionName: "vendorOrderTransactionRecordDtos",
            component: PurchaseOrderTransactionTable,
            visible: true
        },
        {
            title: t("InitialInfoTransactionRecord"),
            collectionName: "fixedAssetsInitialInfoTransactionRecordDtos",
            component: FixedAssetsInitialInfoTransactionRecordTable,
            visible: true
        },
        {
            title: t("CurrentInfoTransactionRecord"),
            collectionName: "fixedAssetsCurrentInfoTransactionRecordDtos",
            component: FixedAssetsCurrentInfoTransactionRecordTable,
            visible: true
        },
        {
            title: t("ConstructionObjectTransactionRecord"),
            collectionName: "constructionObjectTransactionRecordDtos",
            component: ConstructionObjectTransactionRecordTable,
            visible: true
        },
        {
            title: t("DepreciationTransactionRecord"),
            collectionName: "fixedAssetDepreciationTransactionRecordDtos",
            component: FixedAssetDepreciationTransactionRecordTable,
            visible: true
        },
        {
            title: t("PriceList"),
            collectionName: "priceListTransactionRecordDtos",
            component: PriceListTransactionTable,
            visible: true
        },
        {
            title: t("OtherExpenses"),
            collectionName: "otherExpenseTransactionRecordDtos",
            component: OtherExpenseTransactionTable,
            visible: true
        },
        {
            title: t("OtherIncomes"),
            collectionName: "otherIncomeTransactionRecordDtos",
            component: OtherIncomeTransactionTable,
            visible: true
        },
        {
            title: t("MoneyToPayCashTransactionRecord"),
            collectionName: "moneyToPayCashTransactionRecordDtos",
            component: MoneyToPayCashTransactionRecordTable,
            visible: true
        },
        {
            title: t("MoneyToPayBankTransactionRecord"),
            collectionName: "moneyToPayBankTransactionRecordDtos",
            component: MoneyToPayBankTransactionRecordTable,
            visible: true
        }, {
            title: t("SettlementAdvanceHolderTransactionRecord"),
            collectionName: "settlementAdvanceHolderTransactionRecordDtos",
            component: SettlementAdvanceHolderTransactionRecordTable,
            visible: true
        }, {
            title: t("CurrentEmployeeTransactionRecord"),
            collectionName: "currentEmployeeTransactionRecordDtos",
            component: CurrentEmployeeTransactionRecordTable,
            visible: true
        }, {
            title: t("DismissalEmployeeTransactionRecord"),
            collectionName: "dismissalEmployeeTransactionRecordDtos",
            component: DismissalEmployeeTransactionRecordTable,
            visible: true
        }, {
            title: t("HireEmployeeTransactionRecord"),
            collectionName: "hireEmployeeTransactionRecordDtos",
            component: HireEmployeeTransactionRecordTable,
            visible: true
        }, {
            title: t("HistoryEmployeeStateTransactionRecord"),
            collectionName: "historyEmployeeStateTransactionRecordDtos",
            component: HistoryEmployeeStateTransactionRecordTable,
            visible: true
        },
        {
            title: t("TransferEmployeeTransactionRecord"),
            collectionName: "transferEmployeeTransactionRecordDtos",
            component: TransferEmployeeTransactionRecordTable,
            visible: true
        }, {
            title: t("AccruedVatTransactionRecord"),
            collectionName: "accruedVatTransactionRecordDtos",
            component: AccruedVatTransactionRecordTable,
            visible: true
        }, {
            title: t("PurchasedVatTransactionRecord"),
            collectionName: "purchasedVatTransactionRecordDtos",
            component: PurchasedVatTransactionRecordTable,
            visible: true
        },
        {
            title: t("DepositLoanTransactionRecord"),
            collectionName: "depositLoanTransactionRecordDtos",
            component: DepositLoanTransactionRecordTable,
            visible: true
        }, {
            title: t("SalaryTransactionRecord"),
            collectionName: "salaryTransactionRecordDtos",
            component: SalaryTransactionRecordTable,
            visible: true
        }, {
            title: t("SalaryPaymentTransactionRecord"),
            collectionName: "salaryPaymentTransactionRecordDtos",
            component: SalaryPaymentTransactionRecordTable,
            visible: true
        }, {
            title: t("AccrualTransactionRecord"),
            collectionName: "accrualTransactionRecordDtos",
            component: AccrualTransactionRecordTable,
            visible: true
        }, {
            title: t("EmployeeSettlementTransactionRecord"),
            collectionName: "employeeSettlementTransactionRecordDtos",
            component: EmployeeSettlementTransactionRecordTable,
            visible: true
        }, {
            title: t("StockReservationTransactionRecord"),
            collectionName: "stockReservationTransactionRecordDtos",
            component: StockReservationTransactionRecordTable,
            visible: true
        }, {
            title: t("GoodsToReceiveTransactionRecord"),
            collectionName: "goodsToReceiveTransactionRecordDtos",
            component: GoodsToReceiveTransactionRecordTable,
            visible: true
        }, {
            title: t("TaxInvoiceJournalTransactionRecord"),
            collectionName: "taxInvoiceJournalTransactionRecordDtos",
            component: TaxInvoiceJournalTransactionRecordTable,
            visible: true
        }, {
            title: t("DistributionOfLandedCostTransactionRecord"),
            collectionName: "distributionOfLandedCostTransactionRecordDtos",
            component: DistributionOfLandedCostTransactionRecordTable,
            visible: true
        }
    ]

    if (isLoading) {
        return (
            <div className="loader-rs">
                <div className="loader-rs-icon mt-5">
                    <Loader/>
                </div>
            </div>
        );
    }

    const findFirstVisibleTabWhatHasElementsAndEnabledVisibleFlag = () => {
        return transactionTabs.find(tab => {
            const transactions: any = ((documentTransactions as any)[tab.collectionName]);
            return transactions && transactions.length > 0 && tab.visible;
        });
    }

    const handleOpenDocument = () => {
        
    }

    if (isError) {
        return <div className={"alert alert-danger"}>{errorMessage}</div>;
    }


    if (!documentTransactions) {
        return <div>No data</div>;
    }

    return (
        <>
            <div className="main-container container-fluid ">
                <div className="card card-body border">
                    <Row>
                        <Col lg={12}>
                            <Button type={"button"} variant={"link"} onClick={handleOpenDocument}><span className={"fw-bold text-dark text-decoration-underline"}>{documentTransactions.documentPresentationName}</span></Button>
                        </Col>
                    </Row>
                    <hr className="mt-1 mb-1"/>
                    <Tabs
                        defaultActiveKey={findFirstVisibleTabWhatHasElementsAndEnabledVisibleFlag()?.collectionName}>
                        {transactionTabs.map((tab, index) => {
                            const transactions: any = ((documentTransactions as any)[tab.collectionName]);
                            if (!transactions || transactions.length === 0) {
                                return null;
                            }

                            return (
                                <Tab key={index}
                                     eventKey={tab.collectionName}
                                     title={`${t(tab.title)} (${transactions.length})`}
                                >
                                    {React.createElement(tab.component, {items: transactions})}
                                </Tab>
                            )
                        })}
                        {/*          {documentTransactions.accountingTransactionRecordDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="TabAccounting"*/}
                        {/*                   title={`${t("Accounting")} (${documentTransactions.accountingTransactionRecordDtos.length})`}>*/}
                        {/*                  <AccountingTransactionTable*/}
                        {/*                      items={documentTransactions.accountingTransactionRecordDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.stockTransactionRecordDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="TabStock"*/}
                        {/*                   title={`${t("Stock")} (${documentTransactions.stockTransactionRecordDtos.length})`}>*/}
                        {/*                  <StockTransactionTable items={documentTransactions.stockTransactionRecordDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.costTransactionRecordDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="TabCost"*/}
                        {/*                   title={`${t("Cost")} (${documentTransactions.costTransactionRecordDtos.length})`}>*/}
                        {/*                  <CostTransactionTable items={documentTransactions.costTransactionRecordDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.cashTransactionDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="TabCash"*/}
                        {/*                   title={`${t("Cash")} (${documentTransactions.cashTransactionDtos.length})`}>*/}
                        {/*                  <CashTransactionTable items={documentTransactions.cashTransactionDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.bankTransactionRecordDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="TabBank"*/}
                        {/*                   title={`${t("Bank")} (${documentTransactions.bankTransactionRecordDtos.length})`}>*/}
                        {/*                  <BankTransactionTable items={documentTransactions.bankTransactionRecordDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.saleTransactionDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="TabSales"*/}
                        {/*                   title={`${t("Sales")} (${documentTransactions.saleTransactionDtos.length})`}>*/}
                        {/*                  <SalesTransactionTable items={documentTransactions.saleTransactionDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.purchaseTransactionDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="TabPurchase"*/}
                        {/*                   title={`${t("Purchase")} (${documentTransactions.purchaseTransactionDtos.length})`}>*/}
                        {/*                  <PurchaseTransactionTable items={documentTransactions.purchaseTransactionDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          /!*{documentTransactions.settlementCustomerByDocumentMovementTransactionDtos.length > 0 && <Tab*!/*/}
                        {/*          /!*    eventKey="TabSettlementCustomerByDocument"*!/*/}
                        {/*          /!*    title={`${t("SettlementCustomerByDocument")} (${documentTransactions.settlementCustomerByDocumentMovementTransactionDtos.length})`}*!/*/}
                        {/*          /!*>*!/*/}
                        {/*          /!*    <SettlementCustomerByDocumentMovementTable*!/*/}
                        {/*          /!*        items={documentTransactions.settlementCustomerByDocumentMovementTransactionDtos}/>*!/*/}
                        {/*          /!*</Tab>}*!/*/}
                        {/*          /!*{documentTransactions.settlementVendorByDocumentMovementTransactionDtos.length > 0 && <Tab*!/*/}
                        {/*          /!*    eventKey="TabSettlementVendorByDocument"*!/*/}
                        {/*          /!*    title={`${t("SettlementVendorByDocument")} (${documentTransactions.settlementVendorByDocumentMovementTransactionDtos.length})`}*!/*/}
                        {/*          /!*>*!/*/}
                        {/*          /!*    <SettlementVendorByDocumentMovementTable*!/*/}
                        {/*          /!*        items={documentTransactions.settlementVendorByDocumentMovementTransactionDtos}/>*!/*/}
                        {/*          /!*</Tab>}*!/*/}
                        {/*          {documentTransactions.settlementCustomerTransactionRecordDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="TabSettlementCustomer"*/}
                        {/*                   title={`${t("SettlementCustomer")} (${documentTransactions.settlementCustomerTransactionRecordDtos.length})`}>*/}
                        {/*                  <SettlementCustomerMovementTable*/}
                        {/*                      items={documentTransactions.settlementCustomerTransactionRecordDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.settlementVendorTransactionRecordDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="TabSettlementVendor"*/}
                        {/*                   title={`${t("SettlementVendor")} (${documentTransactions.settlementVendorTransactionRecordDtos.length})`}>*/}
                        {/*                  <SettlementVendorMovementTable*/}
                        {/*                      items={documentTransactions.settlementVendorTransactionRecordDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.moneyToPayTransactionRecordDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="TabMoneyToPay"*/}
                        {/*                   title={`${t("MoneyToPay")} (${documentTransactions.moneyToPayTransactionRecordDtos.length})`}>*/}
                        {/*                  <MoneyToPayTransactionTable*/}
                        {/*                      items={documentTransactions.moneyToPayTransactionRecordDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.orderTransactionRecordDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="TabOrder"*/}
                        {/*                   title={`${t("OrderTransaction")} (${documentTransactions.orderTransactionRecordDtos.length})`}>*/}
                        {/*                  <OrderTransactionTable items={documentTransactions.orderTransactionRecordDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.vendorOrderTransactionRecordDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="TabVendor"*/}
                        {/*                   title={`${t("VendorOrderTransaction")} (${documentTransactions.vendorOrderTransactionRecordDtos.length})`}>*/}
                        {/*                  <VendorOrderTransactionTable*/}
                        {/*                      items={documentTransactions.vendorOrderTransactionRecordDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.fixedAssetsInitialInfoTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="InitialInfoTransactionRecord"*/}
                        {/*              title={`${t("InitialInfoTransactionRecord")} (${documentTransactions.fixedAssetsInitialInfoTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <FixedAssetsInitialInfoTransactionRecordTable*/}
                        {/*                  items={documentTransactions.fixedAssetsInitialInfoTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.fixedAssetsCurrentInfoTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="CurrentInfoTransactionRecord"*/}
                        {/*              title={`${t("CurrentInfoTransactionRecord")} (${documentTransactions.fixedAssetsCurrentInfoTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <FixedAssetsCurrentInfoTransactionRecordTable*/}
                        {/*                  items={documentTransactions.fixedAssetsCurrentInfoTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.constructionObjectTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="ConstructionObjectTransactionRecord"*/}
                        {/*              title={`${t("ConstructionObjectTransactionRecord")} (${documentTransactions.constructionObjectTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <ConstructionObjectTransactionRecordTable*/}
                        {/*                  items={documentTransactions.constructionObjectTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.fixedAssetDepreciationTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="fixedAssetDepreciationTransactionRecord"*/}
                        {/*              title={`${t("DepreciationTransactionRecord")} (${documentTransactions.fixedAssetDepreciationTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <FixedAssetDepreciationTransactionRecordTable*/}
                        {/*                  items={documentTransactions.fixedAssetDepreciationTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.priceListTransactionRecordDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="priceList"*/}
                        {/*                   title={`${t("PriceList")} (${documentTransactions.priceListTransactionRecordDtos.length})`}>*/}
                        {/*                  <PriceListTransactionTable*/}
                        {/*                      items={documentTransactions.priceListTransactionRecordDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.otherExpenseTransactionRecordDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="otherExpenses"*/}
                        {/*                   title={`${t("OtherExpenses")} (${documentTransactions.otherExpenseTransactionRecordDtos.length})`}>*/}
                        {/*                  <OtherExpenseTransactionTable*/}
                        {/*                      items={documentTransactions.otherExpenseTransactionRecordDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.otherIncomeTransactionRecordDtos.length > 0 &&*/}
                        {/*              <Tab eventKey="otherIncomes"*/}
                        {/*                   title={`${t("OtherIncomes")} (${documentTransactions.otherIncomeTransactionRecordDtos.length})`}>*/}
                        {/*                  <OtherIncomeTransactionTable*/}
                        {/*                      items={documentTransactions.otherIncomeTransactionRecordDtos}/>*/}
                        {/*              </Tab>}*/}
                        {/*          {documentTransactions.moneyToPayCashTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="MoneyToPayCashTransactionRecord"*/}
                        {/*              title={`${t("MoneyToPayCashTransactionRecord")} (${documentTransactions.moneyToPayCashTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <MoneyToPayCashTransactionRecordTable*/}
                        {/*                  items={documentTransactions.moneyToPayCashTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.moneyToPayBankTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="MoneyToPayBankTransactionRecord"*/}
                        {/*              title={`${t("MoneyToPayBankTransactionRecord")} (${documentTransactions.moneyToPayBankTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <MoneyToPayBankTransactionRecordTable*/}
                        {/*                  items={documentTransactions.moneyToPayBankTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.settlementAdvanceHolderTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="SettlementAdvanceHolderTransactionRecord"*/}
                        {/*              title={`${t("SettlementAdvanceHolderTransactionRecord")} (${documentTransactions.settlementAdvanceHolderTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <SettlementAdvanceHolderTransactionRecordTable*/}
                        {/*                  items={documentTransactions.settlementAdvanceHolderTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}

                        {/*          {documentTransactions.currentEmployeeTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="CurrentEmployeeTransactionRecord"*/}
                        {/*              title={`${t("CurrentEmployeeTransactionRecord")} (${documentTransactions.currentEmployeeTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <CurrentEmployeeTransactionRecordTable*/}
                        {/*                  items={documentTransactions.currentEmployeeTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.dismissalEmployeeTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="DismissalEmployeeTransactionRecord"*/}
                        {/*              title={`${t("DismissalEmployeeTransactionRecord")} (${documentTransactions.dismissalEmployeeTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <DismissalEmployeeTransactionRecordTable*/}
                        {/*                  items={documentTransactions.dismissalEmployeeTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.hireEmployeeTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="HireEmployeeTransactionRecord"*/}
                        {/*              title={`${t("HireEmployeeTransactionRecord")} (${documentTransactions.hireEmployeeTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <HireEmployeeTransactionRecordTable*/}
                        {/*                  items={documentTransactions.hireEmployeeTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.historyEmployeeStateTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="HistoryEmployeeStateTransactionRecord"*/}
                        {/*              title={`${t("HistoryEmployeeStateTransactionRecord")}*/}
                        {/*(${documentTransactions.historyEmployeeStateTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <HistoryEmployeeStateTransactionRecordTable*/}
                        {/*                  items={documentTransactions.historyEmployeeStateTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.transferEmployeeTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="TransferEmployeeTransactionRecord"*/}
                        {/*              title={`${t("TransferEmployeeTransactionRecord")}*/}
                        {/*(${documentTransactions.transferEmployeeTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <TransferEmployeeTransactionRecordTable*/}
                        {/*                  items={documentTransactions.transferEmployeeTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.accruedVatTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="AccruedVatTransactionRecord"*/}
                        {/*              title={`${t("AccruedVatTransactionRecord")}*/}
                        {/*(${documentTransactions.accruedVatTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <AccruedVatTransactionRecordTable*/}
                        {/*                  items={documentTransactions.accruedVatTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.accountingTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="PurchasedVatTransactionRecord"*/}
                        {/*              title={`${t("PurchasedVatTransactionRecord")}*/}
                        {/*(${documentTransactions.purchasedVatTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <PurchasedVatTransactionRecordTable*/}
                        {/*                  items={documentTransactions.purchasedVatTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.accountingTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="depositLoanTransactionRecordDtos"*/}
                        {/*              title={`${t("DepositLoanTransactionRecord")}*/}
                        {/*(${documentTransactions.depositLoanTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <DepositLoanTransactionRecordTable*/}
                        {/*                  items={documentTransactions.depositLoanTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.accountingTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="SalaryTransactionRecord"*/}
                        {/*              title={`${t("SalaryTransactionRecord")}*/}
                        {/*(${documentTransactions.salaryTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <SalaryTransactionRecordTable*/}
                        {/*                  items={documentTransactions.salaryTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.accountingTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="SalaryPaymentTransactionRecord"*/}
                        {/*              title={`${t("SalaryPaymentTransactionRecord")}*/}
                        {/*(${documentTransactions.salaryPaymentTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <SalaryPaymentTransactionRecordTable*/}
                        {/*                  items={documentTransactions.salaryPaymentTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.accountingTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="SalaryPaymentTransactionRecord"*/}
                        {/*              title={`${t("SalaryPaymentTransactionRecord")}*/}
                        {/*(${documentTransactions.salaryPaymentTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <SalaryPaymentTransactionRecordTable*/}
                        {/*                  items={documentTransactions.salaryPaymentTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.accountingTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="accrualTransactionRecordDtos"*/}
                        {/*              title={`${t("AccrualTransactionRecord")}*/}
                        {/*(${documentTransactions.accrualTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <AccrualTransactionRecordTable*/}
                        {/*                  items={documentTransactions.accrualTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.accountingTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="employeeSettlementTransactionRecordDtos"*/}
                        {/*              title={`${t("EmployeeSettlementTransactionRecord")}*/}
                        {/*(${documentTransactions.employeeSettlementTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <EmployeeSettlementTransactionRecordTable*/}
                        {/*                  items={documentTransactions.employeeSettlementTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.accountingTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="stockReservationTransactionRecordDtos"*/}
                        {/*              title={`${t("StockReservationTransactionRecord")}*/}
                        {/*(${documentTransactions.stockReservationTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <StockReservationTransactionRecordTable*/}
                        {/*                  items={documentTransactions.stockReservationTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}

                        {/*          {documentTransactions.accountingTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="goodsToReceiveTransactionRecordDtos"*/}
                        {/*              title={`${t("GoodsToReceiveTransactionRecord")}*/}
                        {/*(${documentTransactions.goodsToReceiveTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <GoodsToReceiveTransactionRecordTable*/}
                        {/*                  items={documentTransactions.goodsToReceiveTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.accountingTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="taxInvoiceJournalTransactionRecordDtos"*/}
                        {/*              title={`${t("TaxInvoiceJournalTransactionRecord")}*/}
                        {/*(${documentTransactions.taxInvoiceJournalTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <TaxInvoiceJournalTransactionRecordTable*/}
                        {/*                  items={documentTransactions.taxInvoiceJournalTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                        {/*          {documentTransactions.accountingTransactionRecordDtos.length > 0 && <Tab*/}
                        {/*              eventKey="distributionOfLandedCostTransactionRecordDtos"*/}
                        {/*              title={`${t("DistributionOfLandedCostTransactionRecord")}*/}
                        {/*(${documentTransactions.distributionOfLandedCostTransactionRecordDtos.length})`}*/}
                        {/*          >*/}
                        {/*              <DistributionOfLandedCostTransactionRecordTable*/}
                        {/*                  items={documentTransactions.distributionOfLandedCostTransactionRecordDtos}/>*/}
                        {/*          </Tab>}*/}
                    </Tabs>
                </div>
            </div>
        </>
    );
};

export default TransactionsPage;
